/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import HomeTourState from '../states/HomeTourState';
import { slug } from '../../common';
import MobileTour from '../components/MobileTour';
import Application from 'flarum/common/Application';

declare module 'flarum/forum/ForumApplication' {
	export default interface ForumApplication {
		/** The desktop tour state, set while a home tour is running. */
		tourState?: HomeTourState;
		/** Set while the user sits on the post-registration welcome page. */
		newRegistration?: boolean;
		/** Set by the login flow when the user has just signed in. */
		newLogin?: boolean;
	}
}

let lastTourStart: number | null = null;
const TOUR_START_THRESHOLD = 2000; // 2 seconds

const startHomeTour = (immediately = false) => {
	const currentTime = Date.now();

	if (!immediately && lastTourStart && currentTime - lastTourStart < TOUR_START_THRESHOLD) {
		return; // too soon after the last start, likely a duplicate
	}

	lastTourStart = currentTime; // update the last tour start time

	let disabled = app.session.user!.preferences()!.homeTourDisabled;

	try {
		if (
			(app.current.data as { routeName?: string }).routeName === 'page' &&
			Number.parseInt(m.route.param('id')) === Number.parseInt(app.forum.attribute<string>('glowingblue-welcome-page.page_id'))
		) {
			disabled = true;
			app.newRegistration = true;
		} else {
			delete app.newRegistration;
		}
	} catch (e) {
		// console.log(e);
	}

	if (disabled && !immediately) {
		return;
	}
	let tourDelay = app.forum.attribute<number | null>(`${slug}.tour_delay`) ?? 1;

	if (immediately) {
		tourDelay = 0;
	}

	// Check if mobile with a media query
	const isMobile = window.matchMedia('(max-width: 767.98px)').matches;

	if (isMobile) {
		// Mobile tour
		setTimeout(() => {
			m.mount(document.getElementById('mobile-tour')!, MobileTour);
		}, tourDelay * 1000);
	} else {
		app.tourState = new HomeTourState();
		setTimeout(() => {
			app.tourState!.start();
		}, tourDelay * 1000);
	}
};

export default function () {
	extend(HeaderSecondary.prototype, 'oncreate', () => {
		if (app.session.user) {
			setTimeout(() => {
				startHomeTour();
			}, 100);
		}
	});

	extend(HeaderSecondary.prototype, 'onupdate', () => {
		if ((app.session.user && !app.tourState && app.newLogin) || app.newRegistration) {
			setTimeout(() => {
				startHomeTour();
			}, 100);
		}
	});

	extend(SessionDropdown.prototype, 'items', (items) => {
		items.add(
			'home-tour',
			<Button icon="far fa-map-signs" onclick={() => startHomeTour(true)}>
				{app.translator.trans('glowingblue-orell-thalia-common.forum.tour.home.start_tour')}
			</Button>,
			10
		);
	});

	// @ts-expect-error -- deliberate patch of the protected `Application.mount`
	extend(Application.prototype, 'mount', () => {
		const mobileTour = document.createElement('div');
		mobileTour.id = 'mobile-tour';
		document.body.appendChild(mobileTour);
	});
}
